/* eslint-disable max-len */
import {
  ReactElement,
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';

import { useNavigate } from 'react-router-dom';

import EmptyTour from '../components/EmptyTour/EmptyTour';
import Search from '../components/Search/Search';
import { FolderCard } from '../components/FolderCard/FolderCard';
import { message } from 'antd';

import { useAppSelector } from 'app/hooks';
import { useFolders } from 'app/hooks/useFolders';
import {
  getFolderCollaborators,
  shareFolder,
  unshareFolder,
  syncFolderTourCollaborators,
  type FolderCollaboratorInfo,
} from 'app/services/firebase/functions';

import { TourCard } from '../components/TourCard/TourCard';

import { Tour } from 'app/types';
import { getLastId } from 'app/helpers';

enum SortBy {
  MostViewed = 'Most Viewed',
  DateCreated = 'Date Created',
  Name = 'Name',
}

type SortDir = 'asc' | 'desc';

const SORT_OPTIONS: SortBy[] = [
  SortBy.MostViewed,
  SortBy.DateCreated,
  SortBy.Name,
];

const SkeletonCard = () => (
  <div className="bg-white border border-black/10 rounded-[18px] shadow-[0_0_10px_rgba(0,0,0,0.06)] w-full flex items-center gap-5 px-5 py-4">
    <div className="w-20 h-20 rounded-xl bg-black/10 shrink-0 animate-pulse" />
    <div className="flex flex-col gap-1.5 flex-1">
      <div className="h-4 rounded bg-black/10 w-2/3 animate-pulse" />
      <div className="h-3 rounded bg-black/5 w-1/3 animate-pulse" />
    </div>
  </div>
);

const SortArrow = ({ className }: { className?: string }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={classNames('transition-transform duration-150', className)}
  >
    <path d="M8 2.5v11M4 10l4 4 4-4" />
  </svg>
);

const PlusIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const NewFolderIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 7.5A1.5 1.5 0 0 1 4.5 6h4l2 2H19.5A1.5 1.5 0 0 1 21 9.5V18a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 18V7.5Z" />
    <path d="M12 11v5M9.5 13.5h5" />
  </svg>
);

const BackArrow = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M10 3L5 8l5 5" />
  </svg>
);

const AVATAR_BG = [
  'bg-[#c93e7a]',
  'bg-[#3e87c9]',
  'bg-[#3eb88a]',
  'bg-[#c9923e]',
  'bg-[#8a5cc9]',
];

const initialOf = (c: { displayName?: string | null; email?: string | null }) =>
  (c.displayName?.[0] || c.email?.[0] || '?').toUpperCase();

const SharedFolderAvatars = ({
  items,
  onClick,
}: {
  items: FolderCollaboratorInfo[];
  onClick: () => void;
}): ReactElement => {
  const shown = items.slice(0, 2);
  const extra = items.length - shown.length;
  return (
    <button
      type="button"
      onClick={onClick}
      title={`Shared with ${items.length} ${
        items.length === 1 ? 'person' : 'people'
      }`}
      className="inline-flex items-center bg-transparent border-0 p-0 cursor-pointer focus:outline-none rounded-full hover:opacity-90 transition-opacity"
    >
      <div className="flex -space-x-2">
        {shown.map((c, i) => (
          <span
            key={c.uid}
            className={classNames(
              'w-7 h-7 rounded-full text-white flex items-center justify-center text-[11px] font-semibold ring-2 ring-white shadow-sm',
              AVATAR_BG[i % AVATAR_BG.length],
            )}
          >
            {initialOf(c)}
          </span>
        ))}
        {extra > 0 && (
          <span className="w-7 h-7 rounded-full bg-black/70 text-white flex items-center justify-center text-[10px] font-semibold ring-2 ring-white shadow-sm">
            +{extra}
          </span>
        )}
      </div>
    </button>
  );
};

interface FolderShareModalProps {
  folderName: string;
  folderId: string;
  ownerUid: string;
  isOwner: boolean;
  initialCollaborators: FolderCollaboratorInfo[];
  onClose: () => void;
  onChange: (next: FolderCollaboratorInfo[]) => void;
}

const FolderShareModal = ({
  folderName,
  folderId,
  ownerUid,
  isOwner,
  initialCollaborators,
  onClose,
  onChange,
}: FolderShareModalProps): ReactElement => {
  const [collabs, setCollabs] =
    useState<FolderCollaboratorInfo[]>(initialCollaborators);
  const [email, setEmail] = useState('');
  const [adding, setAdding] = useState(false);

  const apply = (next: FolderCollaboratorInfo[]) => {
    setCollabs(next);
    onChange(next);
  };

  const handleAdd = async () => {
    const trimmed = email.trim();
    if (!trimmed) return;
    try {
      setAdding(true);
      const res = await shareFolder(folderId, trimmed, ownerUid);
      apply([...collabs, res.collaborator]);
      setEmail('');
      message.success(`${res.collaborator.email} added`);
      syncFolderTourCollaborators(folderId, ownerUid).catch((err) => {
        console.warn('[Dashboard] cascade after share failed', err);
      });
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : 'Failed to add collaborator';
      message.error(msg);
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (uid: string) => {
    try {
      await unshareFolder(folderId, uid, ownerUid);
      apply(collabs.filter((c) => c.uid !== uid));
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : 'Failed to remove collaborator';
      message.error(msg);
    }
  };

  return createPortal(
    <div
      onClick={onClose}
      className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex items-center justify-center px-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-[18px] shadow-[0_8px_32px_rgba(0,0,0,0.18)] w-full max-w-md max-h-[80vh] overflow-y-auto"
      >
        <header className="relative px-6 py-4 border-b border-black/[0.08]">
          <h3 className="text-[15px] font-semibold text-black/85 m-0 pr-8">
            Share &ldquo;{folderName}&rdquo;
          </h3>
          <p className="text-[12px] text-black/55 mt-1 m-0 pr-8">
            Collaborators can see this folder and the tours inside it that they
            already have access to.
          </p>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute top-3 right-3 p-0 bg-transparent border-none text-black/50 hover:text-black/85 transition-colors cursor-pointer"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4 4L12 12M12 4L4 12" />
            </svg>
          </button>
        </header>
        <div className="p-6">
          {isOwner && (
            <div className="flex items-center gap-2 mb-5">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                placeholder="Invite collaborator by email"
                disabled={adding}
                className="flex-1 min-w-0 h-10 px-3 bg-white border border-black/10 rounded-lg text-[14px] text-black/85 placeholder:text-black/40 outline-none focus:border-black/40 transition-colors"
              />
              <button
                type="button"
                onClick={handleAdd}
                disabled={adding || !email.trim()}
                className="btn-primary shrink-0"
              >
                {adding ? 'Adding…' : 'Add'}
              </button>
            </div>
          )}

          {collabs.length === 0 ? (
            <p className="text-[13px] text-black/50 m-0 py-4 text-center">
              Nobody else has access yet.
            </p>
          ) : (
            <div className="flex flex-col gap-3">
              {collabs.map((c, i) => (
                <div key={c.uid} className="flex items-center gap-3 py-1">
                  <div
                    className={classNames(
                      'w-9 h-9 rounded-full text-white flex items-center justify-center text-[11px] font-semibold shrink-0 shadow-sm',
                      AVATAR_BG[i % AVATAR_BG.length],
                    )}
                  >
                    {initialOf(c)}
                  </div>
                  <div className="flex-1 min-w-0">
                    {c.displayName && (
                      <p className="text-[13px] font-medium text-black/85 m-0 truncate">
                        {c.displayName}
                      </p>
                    )}
                    <p className="text-[12px] text-black/50 m-0 truncate">
                      {c.email || c.uid}
                    </p>
                  </div>
                  {isOwner && (
                    <button
                      type="button"
                      onClick={() => handleRemove(c.uid)}
                      className="btn-toolbar text-black/40 hover:text-red-500"
                      aria-label={`Remove ${c.email}`}
                    >
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M4 4L12 12M12 4L4 12" />
                      </svg>
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

function Dashboard(): ReactElement {
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState<SortBy>(SortBy.DateCreated);
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const sortMenuRef = useRef<HTMLDivElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const { data: allTours, loading: isToursLoading } = useAppSelector(
    (state) => state.tours,
  );
  const {
    isAuthLoading,
    fetchUserLoading,
    user: authUser,
  } = useAppSelector((state) => state.authState);
  // Treat as "loading" only on the first fetch (no data yet). Background
  // polls flip isToursLoading on/off — without this gate, we'd flash skeleton
  // cards every 30s when the polling refresh runs.
  const isLoading =
    (isAuthLoading || fetchUserLoading || isToursLoading) &&
    allTours.length === 0;
  const [foundTours, setFoundTours] = useState(allTours);

  // Folder state — localStorage-backed prototype
  const folderApi = useFolders();
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const newFolderInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (creatingFolder) {
      newFolderInputRef.current?.focus({ preventScroll: true });
    }
  }, [creatingFolder]);

  useEffect(() => {
    setFoundTours(allTours);
  }, [allTours]);

  useEffect(() => {
    if (!sortMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        sortMenuRef.current &&
        !sortMenuRef.current.contains(e.target as Node)
      ) {
        setSortMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [sortMenuOpen]);

  // If the current folder is deleted elsewhere, drop back to root
  useEffect(() => {
    if (
      currentFolderId &&
      !folderApi.folders.some((f) => f.id === currentFolderId)
    ) {
      setCurrentFolderId(null);
    }
  }, [currentFolderId, folderApi.folders]);

  const toursToDisplay = useMemo(() => {
    const dirSign = sortDir === 'desc' ? -1 : 1;
    return [...foundTours].sort((a, b) => {
      if (sortBy === SortBy.Name) {
        return (
          (a.name ?? '').localeCompare(b.name ?? '', undefined, {
            sensitivity: 'base',
          }) * dirSign
        );
      }
      if (sortBy === SortBy.DateCreated) {
        const aDate = a.dateCreated?._seconds ?? 0;
        const bDate = b.dateCreated?._seconds ?? 0;
        return (aDate - bDate) * dirSign;
      }
      const aViews = a.views ?? 0;
      const bViews = b.views ?? 0;
      return (aViews - bViews) * dirSign;
    });
  }, [foundTours, sortBy, sortDir]);

  const currentFolder = useMemo(
    () => folderApi.folders.find((f) => f.id === currentFolderId) ?? null,
    [folderApi.folders, currentFolderId],
  );

  const folderPath = useMemo(
    () => folderApi.getFolderPath(currentFolderId),
    [folderApi, currentFolderId],
  );

  const foldersAtCurrentLevel = useMemo(
    () => folderApi.getChildFolders(currentFolderId),
    [folderApi, currentFolderId],
  );

  // Folder collaborators for the share indicator next to "New folder".
  // Only loaded when inside a folder that has at least one collaborator.
  const [folderCollabs, setFolderCollabs] = useState<{
    folderId: string;
    items: FolderCollaboratorInfo[];
  } | null>(null);
  const [folderShareOpen, setFolderShareOpen] = useState(false);
  // Inline rename of the open folder's title — mirrors the tour-name edit
  // flow in TourView (pencil → input + Cancel/Save).
  const [renamingFolder, setRenamingFolder] = useState(false);
  const [folderRenameValue, setFolderRenameValue] = useState('');
  // Leaving the folder (or switching folders) discards any in-progress rename.
  useEffect(() => {
    setRenamingFolder(false);
  }, [currentFolderId]);
  const commitFolderRename = () => {
    if (!currentFolder) return;
    const next = folderRenameValue.trim();
    if (next && next !== currentFolder.name) {
      folderApi.renameFolder(currentFolder.id, next);
    }
    setRenamingFolder(false);
  };
  const folderHasShares =
    !!currentFolder && (currentFolder.collaboratorUids?.length ?? 0) > 0;
  const collabSig = currentFolder?.collaboratorUids?.join(',') ?? '';
  useEffect(() => {
    if (!currentFolder || !folderHasShares) {
      setFolderCollabs(null);
      return undefined;
    }
    let cancelled = false;
    getFolderCollaborators(currentFolder.id, currentFolder.ownerUid)
      .then((res) => {
        if (!cancelled) {
          setFolderCollabs({
            folderId: currentFolder.id,
            items: res.collaborators,
          });
        }
      })
      .catch((err) => {
        console.warn('[Dashboard] folder collabs failed', err);
      });
    return () => {
      cancelled = true;
    };
  }, [currentFolder, folderHasShares, collabSig]);

  const isSearching = searchQuery.trim().length > 0;

  // Scope the tour list based on folder + search state.
  // - Searching: flat match across the whole library
  // - In a folder: tours belonging to that folder
  // - Root: tours not in any folder ("unfiled")
  const scopedTours = useMemo(() => {
    if (isSearching) return toursToDisplay;
    if (currentFolder) {
      const ids = new Set(currentFolder.tourIds);
      return toursToDisplay.filter((t) => ids.has(t.full_id));
    }
    const filed = new Set(folderApi.folders.flatMap((f) => f.tourIds));
    return toursToDisplay.filter((t) => !filed.has(t.full_id));
  }, [toursToDisplay, currentFolder, folderApi.folders, isSearching]);

  // Recursive count — includes tours in the folder itself AND all its descendants.
  const folderTourCount = useCallback(
    (folderId: string) => {
      const alive = new Set(allTours.map((t) => t.full_id));
      const byParent = new Map<string | null, string[]>();
      const tourIdsById = new Map<string, string[]>();
      for (const f of folderApi.folders) {
        const key: string | null = f.parentId ?? null;
        const arr: string[] = byParent.get(key) ?? [];
        arr.push(f.id);
        byParent.set(key, arr);
        tourIdsById.set(f.id, f.tourIds);
      }
      const counted = new Set<string>();
      const stack: string[] = [folderId];
      while (stack.length) {
        const id = stack.pop();
        if (!id) break;
        const tours = tourIdsById.get(id) ?? [];
        for (const t of tours) if (alive.has(t)) counted.add(t);
        const children = byParent.get(id) ?? [];
        for (const c of children) stack.push(c);
      }
      return counted.size;
    },
    [allTours, folderApi.folders],
  );

  const handleSelectTour = useCallback(
    (tour: Tour) =>
      navigate(`/personal/dashboard/tour/${getLastId(tour.full_id)}`),
    [navigate],
  );

  const handleCreateFolderFromHeader = useCallback(() => {
    const name = newFolderName.trim();
    if (!name) {
      setCreatingFolder(false);
      setNewFolderName('');
      return;
    }
    folderApi.createFolder(name, currentFolderId);
    setNewFolderName('');
    setCreatingFolder(false);
  }, [folderApi, newFolderName, currentFolderId]);

  const handleCreateFolderFromMenu = useCallback(
    (name: string) => folderApi.createFolder(name, currentFolderId).id,
    [folderApi, currentFolderId],
  );

  if (!isLoading && !allTours.length) {
    return (
      <div className="flex-1 bg-white relative m-0 overflow-y-auto py-10 px-10 max-md:px-6 max-md:-mx-2 max-md:-mb-2">
        <EmptyTour />
      </div>
    );
  }

  const showFolderSection = !isSearching && foldersAtCurrentLevel.length > 0;
  const showNewFolderCtl = !isSearching;
  // On the main page (no folder open), the New folder + sort controls move
  // to a row beneath the folder list instead of sitting in the top header.
  const showHeaderControls = !!currentFolder;

  const newFolderControl = showNewFolderCtl ? (
    <button
      type="button"
      onClick={() => {
        setNewFolderName('');
        setCreatingFolder(true);
      }}
      className="inline-flex items-center gap-1.5 text-[13px] font-medium text-black/60 hover:text-black/90 transition-colors bg-transparent border-0 p-0 focus:outline-none whitespace-nowrap"
    >
      <NewFolderIcon />
      New folder
    </button>
  ) : null;

  const sortControl = (
    <div
      ref={sortMenuRef}
      className="relative inline-flex items-center gap-2 text-[13px] font-medium text-black/60"
    >
      <button
        type="button"
        aria-label={sortDir === 'desc' ? 'Sort ascending' : 'Sort descending'}
        onClick={() => setSortDir((prev) => (prev === 'desc' ? 'asc' : 'desc'))}
        className="inline-flex items-center justify-center text-black/60 hover:text-black/90 transition-colors bg-transparent border-0 p-0 focus:outline-none"
      >
        <SortArrow className={sortDir === 'asc' ? 'rotate-180' : ''} />
      </button>
      <button
        type="button"
        onClick={() => setSortMenuOpen((o) => !o)}
        className="text-black/60 hover:text-black/90 transition-colors bg-transparent border-0 p-0 focus:outline-none whitespace-nowrap"
      >
        {sortBy}
      </button>
      {sortMenuOpen && (
        <div className="glass-popover absolute top-full right-0 mt-2 min-w-[160px] p-1 z-20">
          {SORT_OPTIONS.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => {
                if (option === sortBy) {
                  setSortDir((prev) => (prev === 'desc' ? 'asc' : 'desc'));
                } else {
                  setSortBy(option);
                }
                setSortMenuOpen(false);
              }}
              className={classNames(
                'block w-full text-left px-3 py-2 rounded-md text-[13px] font-medium transition-colors bg-transparent border-0 focus:outline-none',
                option === sortBy
                  ? 'bg-black/[0.06] text-black/85'
                  : 'text-black/65 hover:bg-black/[0.04] hover:text-black/85',
              )}
            >
              {option}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const folderControls = <>{sortControl}</>;

  return (
    <div className="flex-1 bg-white relative overflow-y-auto py-8 px-8 max-md:py-6 max-md:px-5 max-md:pb-10">
      {creatingFolder &&
        createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
            <div
              className="bg-white rounded-2xl shadow-[0_10px_40px_rgba(0,0,0,0.18)] w-[360px] max-w-[90vw] p-5 pointer-events-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-[15px] font-semibold text-[#151A30] m-0 mb-3">
                New folder
              </h3>
              <input
                ref={newFolderInputRef}
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleCreateFolderFromHeader();
                  } else if (e.key === 'Escape') {
                    setCreatingFolder(false);
                    setNewFolderName('');
                  }
                }}
                placeholder="Folder name"
                className="w-full px-3 py-2 bg-white border border-black/15 rounded-md text-[13px] text-black/85 outline-none focus:border-black/40 transition-colors"
              />
              <div className="flex justify-end gap-2 mt-4">
                <button
                  type="button"
                  onClick={() => {
                    setCreatingFolder(false);
                    setNewFolderName('');
                  }}
                  className="h-9 px-4 rounded-md text-[13px] font-semibold text-black/70 hover:bg-black/[0.04] transition-colors bg-transparent border-0 focus:outline-none"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleCreateFolderFromHeader}
                  disabled={!newFolderName.trim()}
                  className="h-9 px-4 rounded-md text-[13px] font-semibold text-white bg-[var(--ink-5)] hover:bg-[var(--ink)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors border-0 focus:outline-none"
                >
                  Create
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
      <div className="flex items-center gap-4 pt-2 pb-3 mb-3 max-md:flex-wrap">
        <div className="flex-1">
          {currentFolder ? (
            <div className="flex flex-col">
              <nav className="flex items-center gap-1 text-[12px] font-semibold uppercase tracking-wider text-black/50 mb-1 flex-wrap">
                <button
                  type="button"
                  onClick={() => setCurrentFolderId(null)}
                  className="inline-flex items-center gap-1.5 hover:text-black/85 transition-colors bg-transparent border-0 p-0 focus:outline-none"
                >
                  <BackArrow />
                  Dashboard
                </button>
                {folderPath.slice(0, -1).map((f) => (
                  <span
                    key={f.id}
                    className="inline-flex items-center gap-1 min-w-0"
                  >
                    <span className="text-black/30">/</span>
                    <button
                      type="button"
                      onClick={() => setCurrentFolderId(f.id)}
                      className="truncate max-w-[180px] hover:text-black/85 transition-colors bg-transparent border-0 p-0 focus:outline-none"
                    >
                      {f.name}
                    </button>
                  </span>
                ))}
              </nav>
              {renamingFolder ? (
                <div className="flex flex-col gap-2 max-w-md">
                  <input
                    autoFocus
                    value={folderRenameValue}
                    onChange={(e) => setFolderRenameValue(e.target.value)}
                    placeholder="Folder name"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        commitFolderRename();
                      } else if (e.key === 'Escape') {
                        e.preventDefault();
                        setRenamingFolder(false);
                      }
                    }}
                    className="w-full px-3 py-2 border border-black/15 rounded-xl text-[20px] font-bold text-[#151A30] outline-none focus:border-black/40 transition-colors"
                  />
                  <div className="flex items-center gap-2 mt-1">
                    <button
                      type="button"
                      onClick={() => setRenamingFolder(false)}
                      className="inline-flex items-center h-9 px-4 rounded-full text-[13px] font-medium text-black/70 bg-transparent hover:bg-black/[0.04] border border-black/10 transition-colors cursor-pointer focus:outline-none"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={commitFolderRename}
                      disabled={!folderRenameValue.trim()}
                      className="inline-flex items-center h-9 px-4 rounded-full text-[13px] font-semibold text-white bg-black/85 hover:bg-black transition-colors border-0 cursor-pointer focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      Save
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3 min-w-0">
                  <h1 className="text-[26px] font-bold tracking-tight m-0 text-[#151A30] whitespace-nowrap truncate">
                    {currentFolder.name}
                  </h1>
                  {folderApi.isOwner(currentFolder) && (
                    <button
                      type="button"
                      onClick={() => {
                        setFolderRenameValue(currentFolder.name);
                        setRenamingFolder(true);
                      }}
                      aria-label="Rename folder"
                      className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-black/[0.05] hover:bg-black/10 transition-colors border-0 text-black/70 cursor-pointer focus:outline-none shrink-0"
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M11.5 2.5l2 2-9 9-2.5.5.5-2.5 9-9z" />
                      </svg>
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setFolderShareOpen(true)}
                    className="inline-flex items-center gap-1.5 h-8 px-3.5 rounded-full text-[12.5px] font-semibold text-white bg-black/85 hover:bg-black shadow-[0_2px_8px_rgba(0,0,0,0.15)] transition-colors border-0 cursor-pointer focus:outline-none whitespace-nowrap shrink-0"
                  >
                    Share
                  </button>
                </div>
              )}
            </div>
          ) : (
            <h1 className="text-[26px] font-bold tracking-tight m-0 text-[#151A30]">
              Dashboard
            </h1>
          )}
        </div>
        <div className="flex-1 flex justify-center min-w-0 sticky top-0 z-40 bg-white py-2 max-md:flex-none max-md:basis-full max-md:w-full max-md:justify-start">
          <Search
            initData={allTours}
            setData={setFoundTours}
            onQueryChange={setSearchQuery}
            wide
          />
        </div>
        {showHeaderControls && (
          <div className="flex-1 flex justify-end items-center gap-4 min-w-0 max-md:flex-none max-md:order-last max-md:basis-full max-md:w-full max-md:justify-end">
            {currentFolder &&
              folderCollabs &&
              folderCollabs.folderId === currentFolder.id &&
              folderCollabs.items.length > 0 && (
                <SharedFolderAvatars
                  items={folderCollabs.items}
                  onClick={() => setFolderShareOpen(true)}
                />
              )}
          </div>
        )}
      </div>

      {showHeaderControls && (
        <>
          <div className="flex justify-end items-center gap-3 mb-2">
            {newFolderControl}
          </div>
          <div className="flex justify-end items-center gap-4 mb-3">
            {folderControls}
          </div>
        </>
      )}

      {!showHeaderControls && newFolderControl && (
        <div className="flex justify-end items-center gap-4 mb-3">
          {newFolderControl}
        </div>
      )}

      {showFolderSection && (
        <>
          <div className="flex flex-col gap-3 mb-3">
            {foldersAtCurrentLevel.map((folder) => (
              <FolderCard
                key={folder.id}
                folder={folder}
                tourCount={isLoading ? null : folderTourCount(folder.id)}
                ownedByMe={folderApi.isOwner(folder)}
                onOpen={setCurrentFolderId}
                onRename={folderApi.renameFolder}
                onDelete={folderApi.deleteFolder}
              />
            ))}
          </div>
        </>
      )}

      {!showHeaderControls && (
        <div className="flex justify-between items-center gap-4 mb-3">
          <h2 className="text-[15px] font-semibold text-black/85 m-0">
            All tours{!isLoading && ` (${allTours.length})`}
          </h2>
          {sortControl}
        </div>
      )}

      {isLoading ? (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      ) : scopedTours.length === 0 ? (
        <div className="py-16 text-center">
          {isSearching ? (
            <>
              <p className="text-[14px] text-black/55 m-0">
                No results for{' '}
                <span className="text-black/85 font-semibold">
                  &ldquo;{searchQuery}&rdquo;
                </span>
              </p>
              <p className="text-[12px] text-black/40 mt-1 m-0">
                Try a different name or address.
              </p>
            </>
          ) : currentFolder ? (
            foldersAtCurrentLevel.length > 0 ? (
              <p className="text-[14px] text-black/55 m-0">
                No loose tours — everything here is organized in subfolders.
              </p>
            ) : (
              <p className="text-[14px] text-black/55 m-0">
                This folder is empty.
              </p>
            )
          ) : folderApi.folders.length > 0 ? (
            <p className="text-[14px] text-black/55 m-0">
              All tours are organized in folders.
            </p>
          ) : (
            <p className="text-[14px] text-black/55 m-0">
              No tours to display.
            </p>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {scopedTours.map((tour, index) => (
            <TourCard
              key={tour.full_id}
              data={tour}
              onSelect={handleSelectTour}
              priority={index < 8}
              folders={folderApi.folders}
              currentFolderId={
                folderApi.getFolderForTour(tour.full_id)?.id ?? null
              }
              onMoveTour={folderApi.moveTour}
              onCreateFolder={handleCreateFolderFromMenu}
            />
          ))}
        </div>
      )}
      {folderShareOpen && currentFolder && (
        <FolderShareModal
          folderName={currentFolder.name}
          folderId={currentFolder.id}
          ownerUid={currentFolder.ownerUid}
          isOwner={!!authUser && authUser.firebaseId === currentFolder.ownerUid}
          initialCollaborators={
            folderCollabs && folderCollabs.folderId === currentFolder.id
              ? folderCollabs.items
              : []
          }
          onClose={() => setFolderShareOpen(false)}
          onChange={(next) =>
            setFolderCollabs({ folderId: currentFolder.id, items: next })
          }
        />
      )}
    </div>
  );
}

export default Dashboard;
